import {
  logEvent as logEventCore,
  initAnalytics,
  registerMixpanel,
  registerDefaultSegmentations,
} from '@adloid-technologies/analyticssdk-web';

import type { EventType, IEvent } from '@adloid-technologies/analyticssdk-web';
import { getMode } from './common';

export type { EventType, IEvent };

// Effects in child components (landing page, model-load tracking) run before
// the provider's initLog() effect, so events fired that early would hit an
// uninitialised SDK and be dropped. Queue until the SDK is booted.
let sdkReady = false;
const eventQueue: Array<[EventType, IEvent]> = [];
let pendingDefaults: Record<string, string | number | boolean> | null = null;

const flushQueue = () => {
  sdkReady = true;
  if (pendingDefaults) registerDefaultSegmentations(pendingDefaults);
  while (eventQueue.length) {
    const [type, event] = eventQueue.shift()!;
    logEventCore(type, event);
  }
};

export const logEvent = (type: EventType, event: IEvent) => {
  if (!sdkReady) {
    eventQueue.push([type, event]);
    return;
  }
  logEventCore(type, event);
};

/**
 * Registers the default segmentations as super properties so they attach to
 * every event, including ones fired during page teardown (sessionEnd).
 */
export const registerDefaults = (
  props: Record<string, string | number | boolean>
) => {
  pendingDefaults = props;
  if (sdkReady) registerDefaultSegmentations(props);
};

const SESSION_ID_STORAGE_KEY = 'metadome-analytics-session-id';

/** Stable per-tab id, matching the sheet's `sessionId` default segmentation. */
export const getOrCreateSessionId = (): string => {
  const makeId = () => {
    if (typeof crypto !== 'undefined') {
      if (crypto.randomUUID) return crypto.randomUUID();
      if (crypto.getRandomValues) {
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      }
    }
    // No Web Crypto API at all (extremely old/non-standard runtime) — fall
    // back to a non-random but still unique-enough id rather than
    // Math.random(), which CodeQL flags as insecure randomness.
    return `${Date.now()}-${Math.floor(performance?.now?.() ?? 0)}`;
  };
  if (typeof sessionStorage === 'undefined') return makeId();
  const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
  if (existing) return existing;
  const id = makeId();
  sessionStorage.setItem(SESSION_ID_STORAGE_KEY, id);
  return id;
};

export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return 'desktop';
  }
  const ua = navigator.userAgent || '';
  const width = window.innerWidth;
  if (/iPad|Android(?!.*Mobile)/i.test(ua) || (width >= 600 && width < 1024)) {
    return 'tablet';
  }
  if (/Mobi|iPhone|Android/i.test(ua) || width < 600) {
    return 'mobile';
  }
  return 'desktop';
};

/** utm_source/utm_medium/utm_campaign from the current URL, '' when absent. */
export const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
  };
};

/** document.referrer, '' for a direct visit (matches the sheet's "empty if direct"). */
export const getReferrer = (): string => document.referrer || '';

let mixpanelInitStarted = false;

// PostHog EU cloud by default; override with VITE_POSTHOG_HOST (e.g. a reverse proxy).
const POSTHOG_DEFAULT_HOST = 'https://eu.i.posthog.com';
const POSTHOG_DEFAULT_UI_HOST = 'https://eu.posthog.com';

const getPostHogConfig = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!apiKey) return undefined;
  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
    POSTHOG_DEFAULT_HOST;
  return {
    apiKey,
    host,
    // A custom (proxy) host needs the real UI host to be given explicitly.
    uiHost: POSTHOG_DEFAULT_UI_HOST,
    debug: getMode() !== 'main',
  };
};

/**
 * Boots the underlying Mixpanel SDK once per page load. Safe to call from
 * multiple places (e.g. React StrictMode double-invoking effects) — only the
 * first call actually initialises.
 */
export const initLog = (onLoaded?: () => void) => {
  if (mixpanelInitStarted) {
    flushQueue();
    onLoaded?.();
    return;
  }
  mixpanelInitStarted = true;
  const appEnv = getMode();
  initAnalytics(
    'metadome-autodome2-0-web',
    'metadome-autodome2-0-web',
    'webgl',
    appEnv,
    100,
    { log: appEnv === 'dev' },
    () => {
      registerMixpanel('metadome-autodome2-0-web', appEnv);
      flushQueue();
      onLoaded?.();
    },
    undefined,
    'localStorage',
    // sessionEnd needs sendBeacon to survive tab close.
    'sendBeacon',
    getPostHogConfig()
  );
  // Covers the Mixpanel loaded callback not firing (blocked/failed init).
  flushQueue();
};
